/**
 * Settings Page
 * Extracted from App.js - Phase 7G Frontend Refactor
 * Shows system status, business info, dealer location, embed scripts, and demo data
 */
import { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useToast } from "../contexts/ToastContext";

const BUILD_VERSION = {
  version: "1.4.0-reseller"
};

// Resolve backend URL via window.location.origin so this works for
// preview, custom prod domain, and default emergent.host alike.
const getBackendUrl = () => {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};
const BACKEND_URL = getBackendUrl();
// CANONICAL: Always use app.verifieddemand.com for dealer-facing snippets (white-label requirement)
const EMBED_BASE_URL = "https://app.verifieddemand.com";

const SettingsPage = ({ onNavigate } = {}) => {
  const { apiFetch, user } = useAuth();
  const toast = useToast();
  const [business, setBusiness] = useState(null);
  const [offers, setOffers] = useState([]);
  const [systemStatus, setSystemStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const errorShownRef = useRef(false);

  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      try {
        const [bizRes, offersRes, statusRes] = await Promise.all([
          apiFetch("/settings/business", { method: "GET" }),
          apiFetch("/offers", { method: "GET" }),
          apiFetch("/settings/status", { method: "GET" }).catch(() => ({ data: null })),
        ]);
        if (isMounted) {
          setBusiness(bizRes.data);
          setOffers(offersRes.data.filter(o => o.is_active));
          setSystemStatus(statusRes.data);
          setIsLoading(false);
        }
      } catch (e) {
        if (isMounted) {
          console.error(e);
          setIsLoading(false);
          if (!errorShownRef.current) {
            errorShownRef.current = true;
            toast.error("Failed to load settings", "load_settings");
          }
        }
      }
    };
    
    fetchData();
    return () => { isMounted = false; };
  }, [apiFetch, toast]);

  const generateDemoData = async () => {
    setGenerating(true);
    try {
      await apiFetch("/demo/generate", { method: "POST" });
      toast.success("Demo data generated! Refreshing...");
      setTimeout(() => window.location.reload(), 1000);
    } catch (e) {
      toast.error("Error generating demo data");
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (e) {
      toast.info("Please select and copy the code manually");
    }
  };

  if (isLoading) return <div className="loading">Loading...</div>;

  const selectedOfferId = offers[0]?.id || 'YOUR_OFFER_ID';

  const embedSnippet = `<!-- Verified Demand Embed -->
<script
  src="${EMBED_BASE_URL}/api/embed-nocache.js"
  data-public-key="${business?.public_key}"
  async>
</script>`;

  const triggerExample = `<!-- Add data-vd-trigger to any element to open the modal -->
<button data-vd-trigger>Get Your Offer</button>

<!-- Works on any element -->
<a href="#" data-vd-trigger>View Pricing</a>
<div data-vd-trigger class="cta-banner">Click for discount</div>`;

  const isMasterAdmin = user?.role === 'MASTER_ADMIN';
  const isPreview = window.location.hostname.includes('preview.emergentagent.com');

  return (
    <div className="page" data-testid="settings-page">
      <div className="page-header">
        <div className="page-header-left">
          <h2 className="page-title">Settings</h2>
          <p className="page-subtitle">Configure your business, location, and embed script</p>
        </div>
      </div>
      
      {/* System Status — Master Admin only */}
      {isMasterAdmin && (
      <div className="settings-section">
        <h3>System Status</h3>
        <div className="status-grid">
          {systemStatus?.database !== 'healthy' && (
            <div className="status-item">
              <span className="status-label">Database</span>
              <span className="status-value status-warning">○ Not connected</span>
            </div>
          )}
          <div className="status-item">
            <span className="status-label">Database</span>
            <span className={`status-value ${systemStatus?.database === 'healthy' ? 'status-ok' : 'status-warning'}`}>
              {systemStatus?.database === 'healthy' ? '● Connected' : '○ Not connected'}
            </span>
          </div>
          <div className="status-item">
            <span className="status-label">SMS Verification</span>
            <span className={`status-value ${systemStatus?.sms_configured ? 'status-ok' : 'status-warning'}`}>
              {systemStatus?.sms_configured ? '● Configured' : '○ Not configured'}
            </span>
          </div>
          <div className="status-item">
            <span className="status-label">SMS Provider</span>
            <span className="status-value status-ok" style={{textTransform:'uppercase'}}>
              {systemStatus?.sms_provider || 'GHL'}
            </span>
          </div>
          <div className="status-item">
            <span className="status-label">Environment</span>
            <span className={`status-value ${isPreview ? 'status-warning' : 'status-ok'}`}>
              {isPreview ? '○ Preview' : '● Production'}
            </span>
          </div>
          <div className="status-item">
            <span className="status-label">App Version</span>
            <span className="status-value" style={{color:'#6B7280', fontFamily:'monospace', fontSize:'12px'}}>
              {BUILD_VERSION.version}
            </span>
          </div>
        </div>
      </div>
      )}

      {/* Business Info */}
      <div className="settings-section">
        <h3>Business Info</h3>
        <div className="setting-item">
          <label>Business Name</label>
          <div className="setting-value">{business?.name}</div>
        </div>
        <div className="setting-item">
          <label>Public Key</label>
          <div className="setting-value code">{business?.public_key}</div>
        </div>
      </div>

      {/* Dealer Location — moved to General Info (iter93 — Issue 9: deduped address entry) */}
      <div className="settings-section" data-testid="dealer-location-section">
        <h3>Dealership Address</h3>
        <p className="section-description">
          Your dealership&apos;s physical address is now managed in <strong>General Info</strong>.
          This address powers proximity-based Geographic Demand intelligence (ranks nearby ZIP codes by distance from your location).
        </p>
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (onNavigate) onNavigate("general-info");
          }}
          className="btn-secondary"
          style={{ display: "inline-flex", alignItems: "center", gap: "6px", textDecoration: "none" }}
          data-testid="settings-go-to-general-info-btn"
        >
          Edit Address in General Info →
        </a>
      </div>

      {/* Embed Script */}
      <div className="settings-section">
        <h3>Embed Script</h3>
        <p className="section-description">
          Add this script to your website to enable lead capture. Place it before the closing <code>&lt;/body&gt;</code> tag.
        </p>
        
        <div className="embed-step">
          <div className="step-number">1</div>
          <div className="step-content">
            <h4>Add the script tag</h4>
            <pre className="code-block">{embedSnippet}</pre>
            <button 
              className={`btn-secondary ${copySuccess ? 'btn-success' : ''}`} 
              onClick={() => copyToClipboard(embedSnippet)}
            >
              {copySuccess ? '✓ Copied!' : 'Copy Script'}
            </button>
          </div>
        </div>

        <div className="embed-step">
          <div className="step-number">2</div>
          <div className="step-content">
            <h4>Add trigger elements</h4>
            <p className="step-description">Add <code>data-vd-trigger</code> to any element that should open the offer modal:</p>
            <pre className="code-block">{triggerExample}</pre>
          </div>
        </div>

        {offers.length === 0 && (
          <div className="embed-warning">
            ⚠️ You don&apos;t have any active offers. Create an offer first to use the embed.
          </div>
        )}

        {offers.length > 1 && (
          <div className="embed-info">
            ℹ️ Using offer &quot;{offers[0]?.title}&quot;. To use a different offer, replace the <code>data-offer-id</code> value with the desired offer ID.
          </div>
        )}
      </div>

      {/* Demo Data — MASTER_ADMIN only. Dealers shouldn't see seeded sample
          data in their own dashboard; the admin uses this when provisioning
          a new dealer to populate demo metrics for screenshots / training. */}
      {user?.role === "MASTER_ADMIN" && (
        <div className="settings-section">
          <h3>Demo Data</h3>
          <p className="section-description">Generate sample data to test the dashboard features (admin tool — visible to MASTER_ADMIN only).</p>
          <button className="btn-primary" onClick={generateDemoData} disabled={generating} data-testid="generate-demo-btn">
            {generating ? "Generating..." : "Generate Demo Data"}
          </button>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;
